/**
 * Slide-out panel inside the print window: pick which of the record's
 * attached files should be included — merged into the PDF if they're a
 * PDF/image, attached separately to email otherwise.
 */
import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import type { Attachment } from '../AttachmentPanel'
import { isMergeableAttachment } from './usePrintAttachments'

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return <ImageIcon color="primary" fontSize="small" />
  if (contentType === 'application/pdf') return <PictureAsPdfIcon color="error" fontSize="small" />
  return <InsertDriveFileIcon color="action" fontSize="small" />
}

export interface PrintAttachmentsPanelProps {
  attachments: Attachment[]
  selected: Set<string>
  onToggle: (id: string) => void
  onClose: () => void
}

export function PrintAttachmentsPanel({
  attachments,
  selected,
  onToggle,
  onClose,
}: PrintAttachmentsPanelProps) {
  return (
    <Box
      sx={{
        width: 300, flexShrink: 0, borderLeft: 1, borderColor: 'divider',
        display: 'flex', flexDirection: 'column', bgcolor: 'background.paper',
      }}
      data-testid="print-attachments-panel"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Attached files
        </Typography>
        <IconButton size="small" onClick={onClose} data-testid="print-attachments-panel-close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {attachments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No attached files.
        </Typography>
      ) : (
        <List dense sx={{ flex: 1, overflowY: 'auto' }}>
          {attachments.map((att) => (
            <ListItemButton
              key={att.id}
              onClick={() => onToggle(att.id)}
              dense
              data-testid={`print-attachment-row-${att.id}`}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={selected.has(att.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 28 }}>{getFileIcon(att.content_type)}</ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                primary={att.file_name}
              />
              <Chip
                size="small"
                label={isMergeableAttachment(att.content_type) ? 'merges into PDF' : 'email only'}
                color={isMergeableAttachment(att.content_type) ? 'primary' : 'default'}
                variant="outlined"
                sx={{ height: 18, fontSize: 10 }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  )
}
